<template>
    <main class="menu-publico">
        <header class="menu-hero">
            <div class="hero-glow hero-glow-one"></div>
            <div class="hero-glow hero-glow-two"></div>
            <div class="menu-container hero-content">
                <div class="brand-mark" aria-hidden="true"><b-icon icon="silverware-fork-knife"></b-icon></div>
                <p class="eyebrow">CARDÁPIO DIGITAL</p>
                <h1>{{ cardapio ? cardapio.unidadeNome : 'Bem-vindo' }}</h1>
                <p class="hero-subtitle">Escolha com calma. Preparamos tudo para você.</p>
            </div>
        </header>

        <section class="menu-container menu-content">
            <div v-if="carregando" class="state-card" role="status">
                <b-loading :active="true" :is-full-page="false"></b-loading>
                <p>Preparando o cardápio...</p>
            </div>

            <div v-else-if="erroMensagem" class="state-card state-error">
                <b-icon icon="food-off" size="is-large"></b-icon>
                <h2>Não encontramos este cardápio</h2>
                <p>{{ erroMensagem }}</p>
                <b-button type="is-dark" rounded icon-left="refresh" @click="carregar">Tentar novamente</b-button>
            </div>

            <template v-else-if="cardapio">
                <div v-if="cardapio.categorias.length" class="menu-tools">
                    <b-field class="search-field">
                        <b-input v-model="busca" icon="magnify" rounded size="is-medium" placeholder="O que você gostaria de pedir?" aria-label="Pesquisar no cardápio"></b-input>
                    </b-field>

                    <nav class="category-nav" aria-label="Categorias do cardápio">
                        <button v-for="categoria in categoriasVisiveis" :key="categoria.nome" type="button" class="category-chip" :style="{ '--category-color': corSegura(categoria.cor) }" @click="irParaCategoria(categoria.nome)">
                            <span></span>{{ formatarNome(categoria.nome) }}
                        </button>
                    </nav>
                </div>

                <div v-if="categoriasFiltradas.length" class="category-list">
                    <section v-for="categoria in categoriasFiltradas" :id="idCategoria(categoria.nome)" :key="categoria.nome" class="category-section">
                        <div class="category-heading">
                            <span class="category-line" :style="{ backgroundColor: corSegura(categoria.cor) }"></span>
                            <div>
                                <h2>{{ formatarNome(categoria.nome) }}</h2>
                                <p class="category-kicker">{{ categoria.observacao  }}</p>
                            </div>
                            <span class="item-count">{{ categoria.produtos.length }} {{ categoria.produtos.length === 1 ? 'item' : 'itens' }}</span>
                        </div>

                        <div class="product-grid">
                            <article v-for="produto in categoria.produtos" :key="produto.nome" class="product-card">
                                <div class="product-icon" :style="{ backgroundColor: corComTransparencia(categoria.cor), color: corSegura(categoria.cor) }" aria-hidden="true">
                                    {{ inicial(produto.nome) }}
                                </div>
                                <div class="product-info">
                                    <h3>{{ formatarNome(produto.nome) }}</h3>
                                </div>
                                <strong class="product-price">{{ moeda(produto.preco) }}</strong>
                            </article>
                        </div>
                    </section>
                </div>

                <div v-else class="state-card">
                    <b-icon :icon="busca ? 'magnify-close' : 'silverware-clean'" size="is-large"></b-icon>
                    <h2>{{ busca ? 'Nenhum item encontrado' : 'Cardápio em preparação' }}</h2>
                    <p>{{ busca ? 'Tente pesquisar com outro nome.' : 'Em breve teremos novidades por aqui.' }}</p>
                    <b-button v-if="busca" type="is-light" rounded @click="busca = ''">Limpar busca</b-button>
                </div>
            </template>
        </section>

        <footer v-if="cardapio" class="menu-footer">
            <b-icon icon="heart-outline" size="is-small"></b-icon>
            <span>Obrigado pela preferência</span>
            <p class="has-text-centered is-italic">desenvolvido por <a target="blank" href="https://site.myclient.com.br/">MYCLIENT SISTEMAS</a></p>
        </footer>
    </main>
</template>

<script>
import http from '@/http'

export default {
    name: 'CardapioPublico',
    data: () => ({
        cardapio: null,
        busca: '',
        carregando: false,
        erroMensagem: ''
    }),
    computed: {
        unidade() {
            return (this.$route.params.unidade || this.$route.query.unidade || '').toString().trim()
        },
        categoriasVisiveis() {
            return this.cardapio ? this.cardapio.categorias : []
        },
        categoriasFiltradas() {
            const termo = this.normalizar(this.busca)
            if (!termo) return this.categoriasVisiveis

            return this.categoriasVisiveis.map(categoria => ({
                ...categoria,
                produtos: categoria.produtos.filter(produto => this.normalizar(produto.nome).includes(termo))
            })).filter(categoria => categoria.produtos.length)
        }
    },
    watch: {
        unidade: 'carregar'
    },
    created() {
        this.carregar()
    },
    methods: {
        carregar() {
            this.erroMensagem = ''
            this.cardapio = null

            if (!this.unidade) {
                this.erroMensagem = 'O endereço não informa qual unidade deve ser exibida.'
                return
            }

            this.carregando = true
            http.get('/api/publico/cardapio', { params: { unidade: this.unidade } })
                .then(response => { this.cardapio = response.data })
                .catch(error => {
                    this.erroMensagem = error.data?.erro || 'Não foi possível carregar o cardápio. Tente novamente em instantes.'
                })
                .finally(() => { this.carregando = false })
        },
        moeda(valor) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
        },
        normalizar(valor) {
            return (valor || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim()
        },
        formatarNome(valor) {
            return (valor || '').toLocaleLowerCase('pt-BR').replace(/(^|[\s/-])\S/g, letra => letra.toLocaleUpperCase('pt-BR'))
        },
        inicial(nome) {
            return (nome || '?').trim().charAt(0).toLocaleUpperCase('pt-BR')
        },
        corSegura(cor) {
            return /^#[0-9a-f]{6}$/i.test(cor || '') ? cor : '#c96d3b'
        },
        corComTransparencia(cor) {
            return `${this.corSegura(cor)}18`
        },
        idCategoria(nome) {
            return `categoria-${this.normalizar(nome).replace(/[^a-z0-9]+/g, '-')}`
        },
        irParaCategoria(nome) {
            const elemento = document.getElementById(this.idCategoria(nome))
            if (elemento) elemento.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }
}
</script>

<style scoped>
.menu-publico {
    --ink: #17201c;
    --muted: #68746e;
    --paper: #fbfaf6;
    min-height: 100vh;
    color: var(--ink);
    background: var(--paper);
    font-family: "Trebuchet MS", "Segoe UI", sans-serif;
}
.menu-container { width: min(1120px, calc(100% - 2rem)); margin: 0 auto; }
.menu-hero { position: relative; min-height: 310px; overflow: hidden; color: #fff; background: linear-gradient(125deg, #14251e 0%, #244b3b 58%, #b76a3e 145%); }
.hero-content { position: relative; z-index: 1; padding: 4rem 0 3.5rem; }
.hero-content h1 { max-width: 760px; margin: .25rem 0 .6rem; color: #fff; font-family: Georgia, "Times New Roman", serif; font-size: clamp(2.7rem, 8vw, 5.2rem); font-weight: 500; line-height: .98; letter-spacing: -.04em; }
.hero-subtitle { max-width: 520px; color: rgba(255,255,255,.76); font-size: 1.08rem; }
.category-kicker { margin: 0; color: #e8b183; font-size: 1.5rem; font-weight: 800; letter-spacing: .2em; }
.eyebrow { margin: 0; color: #e8b183; font-size: .72rem; font-weight: 800; letter-spacing: .2em; }
.brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; margin-bottom: 1.2rem; color: #203d31; background: #f3cfaa; border-radius: 50%; }
.hero-glow { position: absolute; border: 1px solid rgba(255,255,255,.12); border-radius: 50%; }
.hero-glow-one { top: -240px; right: -100px; width: 600px; height: 600px; }
.hero-glow-two { right: 210px; bottom: -250px; width: 420px; height: 420px; }
.menu-content { position: relative; min-height: 360px; padding: 0 0 4rem; }
.menu-tools { position: sticky; z-index: 5; top: 0; margin: -1.8rem 0 3rem; padding: 1rem; background: rgba(255,255,255,.95); border: 1px solid #e9e6dc; border-radius: 18px; box-shadow: 0 14px 40px rgba(24,43,35,.1); backdrop-filter: blur(12px); }
.search-field { max-width: 620px; margin: 0 auto .8rem !important; }
.category-nav { display: flex; gap: .55rem; overflow-x: auto; padding: .15rem .2rem .1rem; scrollbar-width: thin; }
.category-chip { flex: 0 0 auto; padding: .55rem .85rem; color: #3f4944; background: #f8f7f2; border: 1px solid #e9e6dc; border-radius: 999px; cursor: pointer; font: inherit; font-size: .84rem; font-weight: 700; transition: transform .15s ease, border-color .15s ease; }
.category-chip:hover, .category-chip:focus { border-color: var(--category-color); outline: none; transform: translateY(-1px); }
.category-chip span { display: inline-block; width: 8px; height: 8px; margin-right: .45rem; background: var(--category-color); border-radius: 50%; }
.category-section { scroll-margin-top: 145px; margin-bottom: 3.4rem; }
.category-heading { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.2rem; }
.category-heading h2 { margin: 0; font-family: Georgia, "Times New Roman", serif; font-size: clamp(1.8rem, 4vw, 2.35rem); font-weight: 500; line-height: 1.05; }
.category-kicker { color: #997052; letter-spacing: .13em; white-space: pre-wrap; }
.category-line { width: 5px; height: 48px; border-radius: 8px; }
.item-count { margin-left: auto; color: var(--muted); font-size: 1; white-space: nowrap; }
.product-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .85rem; }
.product-card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 1rem; min-height: 106px; padding: 1.1rem; background: #fff; border: 1px solid #ebe8de; border-radius: 16px; box-shadow: 0 4px 16px rgba(27,43,36,.045); transition: transform .18s ease, box-shadow .18s ease; }
.product-card:hover { transform: translateY(-2px); box-shadow: 0 9px 24px rgba(27,43,36,.09); }
.product-icon { display: flex; visibility: hidden; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 14px; font-family: Georgia, serif; font-size: 1.25rem; font-weight: 700; }
.product-info h3 { margin: 0; font-size: 1.15rem; font-weight: 800; line-height: 1.25; }
.product-price { align-self: center; color: #244b3b; font-size: 1.03rem; white-space: nowrap; }
.state-card { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 280px; margin-top: 2rem; padding: 2rem; text-align: center; background: #fff; border: 1px solid #ebe8de; border-radius: 20px; }
.state-card h2 { margin: 1rem 0 .4rem; font-family: Georgia, serif; font-size: 1.8rem; }
.state-card p { margin: 0 0 1.25rem; color: var(--muted); }
.state-error { color: #88462e; }
.menu-footer { display: flex; align-items: center; justify-content: center; gap: .4rem; padding: 1.7rem; color: #d9e3de; background: #172a22; font-size: .82rem; letter-spacing: .05em; }
@media (max-width: 768px) {
    .menu-hero { min-height: 270px; }
    .hero-content { padding-top: 3rem; }
    .menu-tools { margin-top: -1.4rem; border-radius: 14px; }
    .product-grid { grid-template-columns: 1fr; }
    .product-card { grid-template-columns: auto minmax(0, 1fr); }
    .product-price { grid-column: 2; }
    .category-section { scroll-margin-top: 140px; }
}
@media (max-width: 420px) {
    .menu-container { width: min(100% - 1.2rem, 1120px); }
    .product-card { gap: .75rem; padding: .9rem; }
    .product-icon { width: 42px; height: 42px; }
    .item-count { display: none; }
}
</style>
